import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Application, Graphics } from 'pixi.js';
import {
  StudioAnimation,
  StudioScene,
} from '../../../element-editor/store/studio/studio.types';
import { StudioSceneShape } from '../../../element-editor/shape/shapes/general/root/studio-scene-shape';
import { StudioService } from '../../../element-editor/store/studio';
import { CanvasService } from '../../../services/canvas/canvas.service';
import { LayoutCell } from '../../../element-editor/store/studio/layout/layout-cell';

@Component({
  selector: 'nw-scene-preview',
  templateUrl: './scene-preview.component.html',
  styleUrls: ['./scene-preview.component.scss'],
})
export class ScenePreviewComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() scene: StudioScene | null = null;
  @Input() highlightCell: LayoutCell | null = null;

  @ViewChild('canvas') canvasRef: ElementRef;

  private app: Application | null = null;
  private shape: StudioSceneShape | null = null;
  private highlightGraphics: Graphics | null = null;
  private loopTimeout: ReturnType<typeof setTimeout> | null = null;
  private scale = 1;
  private mounted = false;

  constructor(
    private studioService: StudioService,
    private cs: CanvasService,
  ) {}

  ngAfterViewInit() {
    this.mounted = true;
    if (this.scene) {
      this.rebuild();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.mounted) return;

    if (changes['scene'] && !changes['scene'].firstChange) {
      const prev = changes['scene'].previousValue;
      const curr = changes['scene'].currentValue;
      // Only full rebuild when the scene reference identity changes
      // (i.e. switching to a different scene, not patching the same one)
      if (prev !== curr) {
        this.rebuild();
      }
    }

    if (changes['highlightCell']) {
      this.updateHighlight();
    }
  }

  // --- Public patch methods (called via @ViewChild) ---

  /** Replay a single shape's animation without rebuilding */
  patchShapeAnimation(shapeId: string, animation: StudioAnimation) {
    if (!this.shape) return;
    const child = this.shape.shapes[shapeId];
    if (!child) return;
    child.gsapAnimateStep(animation, null, true);
  }

  /** Update the scene animation chain and replay */
  patchSceneAnimation(animation: StudioAnimation | null) {
    if (!this.shape || !this.scene) return;
    this.scene.animation = animation || undefined;
    this.play();
  }

  // --- Playback controls ---

  async play() {
    if (!this.shape) return;

    this.stopLoop();
    if (this.highlightGraphics) {
      this.highlightGraphics.clear();
    }

    const run = async () => {
      await this.shape.resetAnimation();
      const tl = this.shape.gsapAnimate();
      tl.eventCallback('onComplete', () => {
        this.loopTimeout = setTimeout(() => run(), 2_000);
      });
    };

    run();
  }

  async reset() {
    this.stopLoop();
    if (this.shape) {
      await this.shape.resetAnimation();
    }
    this.updateHighlight();
  }

  // --- Internal ---

  private rebuild() {
    this.cleanup();
    if (!this.scene || !this.canvasRef) return;

    const el = this.canvasRef.nativeElement;
    const containerWidth = el.parentElement?.clientWidth || 480;
    const previewWidth = containerWidth;
    const previewHeight = Math.round(previewWidth * (9 / 16));
    this.scale = previewWidth / this.cs.currentScreenX;

    const app = new Application({
      width: previewWidth,
      height: previewHeight,
      backgroundAlpha: 1,
      backgroundColor: 0xf9fafb,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
    });

    globalThis.__PIXI_APP__ = app;
    el.appendChild(app.view);
    this.app = app;

    const shape = new StudioSceneShape(this.studioService, this.scene, {
      margin: 0,
      preview: true,
    });
    shape.mountPreview(app.stage, this.scale);
    this.shape = shape;

    this.highlightGraphics = new Graphics();
    this.highlightGraphics.scale.set(this.scale);
    app.stage.addChild(this.highlightGraphics);
    this.updateHighlight();
  }

  private updateHighlight() {
    if (!this.highlightGraphics) return;
    this.highlightGraphics.clear();

    if (!this.highlightCell || !this.highlightCell.absoluteDims) return;

    const { x, y, w, h } = this.highlightCell.absoluteDims;
    this.highlightGraphics.lineStyle(2, 0x3b82f6, 0.8);
    this.highlightGraphics.beginFill(0x3b82f6, 0.08);
    this.highlightGraphics.drawRect(
      x * this.cs.currentScreenX,
      y * this.cs.currentScreenY,
      w * this.cs.currentScreenX,
      h * this.cs.currentScreenY,
    );
    this.highlightGraphics.endFill();
  }

  private stopLoop() {
    if (this.loopTimeout) {
      clearTimeout(this.loopTimeout);
      this.loopTimeout = null;
    }
  }

  private cleanup() {
    this.stopLoop();
    if (this.app) {
      this.app.destroy(true, {
        children: true,
        texture: false,
        baseTexture: false,
      });
      this.app = null;
    }
    if (this.shape) {
      this.shape.remove();
      this.shape = null;
    }
    this.highlightGraphics = null;
  }

  /** Expose the underlying StudioSceneShape for direct access */
  get sceneShape(): StudioSceneShape | null {
    return this.shape;
  }

  ngOnDestroy() {
    this.cleanup();
  }
}
